<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import inboxesAPI from 'dashboard/api/inboxes';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const props = defineProps({
  inboxId: { type: [String, Number], required: true },
});

// Capped by Whatsapp::SyncEvolutionOutageService::MAX_LOOKBACK on the server.
const WINDOW_OPTIONS = [6, 24, 72, 168];
const DEFAULT_HOURS = 24;

const { t } = useI18n();
const dialogRef = ref(null);
const isSyncing = ref(false);
const hours = ref(DEFAULT_HOURS);

const options = computed(() =>
  WINDOW_OPTIONS.map(value => ({
    value,
    label: t(`INBOX_MGMT.EVOLUTION_MANUAL_SYNC.WINDOWS.H${value}`),
  }))
);

const open = () => {
  hours.value = DEFAULT_HOURS;
  dialogRef.value?.open();
};

// Fire-and-forget: the import runs in a background job (Whatsapp::SyncEvolutionOutageService),
// so there's nothing to await beyond enqueueing it - messages show up as they're imported.
const handleConfirm = async () => {
  if (isSyncing.value) return;

  isSyncing.value = true;
  try {
    await inboxesAPI.evolutionManualSync(props.inboxId, hours.value);
    useAlert(t('INBOX_MGMT.EVOLUTION_MANUAL_SYNC.SUCCESS_MESSAGE'));
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.EVOLUTION_MANUAL_SYNC.ERROR_MESSAGE')
    );
  } finally {
    isSyncing.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('INBOX_MGMT.EVOLUTION_MANUAL_SYNC.CONFIRM.TITLE')"
    :description="t('INBOX_MGMT.EVOLUTION_MANUAL_SYNC.HELP_TEXT')"
    :confirm-button-label="t('INBOX_MGMT.EVOLUTION_MANUAL_SYNC.CONFIRM.YES')"
    :cancel-button-label="t('INBOX_MGMT.EVOLUTION_MANUAL_SYNC.CONFIRM.NO')"
    :is-loading="isSyncing"
    @confirm="handleConfirm"
  >
    <Select
      v-model="hours"
      :options="options"
      :aria-label="t('INBOX_MGMT.EVOLUTION_MANUAL_SYNC.LABEL')"
    />
  </Dialog>
</template>
